import { useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { getImageDetails } from "../../../../../feature/image-slice";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";

const OrbitLoading = (props: any) => {
  const { loadingClose } = props;
  const imageDetails = useSelector(getImageDetails);
  const ufo = imageDetails?.common[13]?.img;
  const ufoRef = useRef<THREE.Mesh>(null);
  const starsRef = useRef<THREE.LineSegments>(null);

  const ufoTexture = useMemo(() => {
    if (!ufo) return null;
    return new THREE.TextureLoader().load(ufo);
  }, [ufo]);

  const starCount = 2000;
  const starGeometry = useMemo(() => {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(starCount * 6);
    for (let i = 0; i < starCount; i++) {
      const x = (Math.random() - 0.5) * 200;
      const y = (Math.random() - 0.5) * 200;
      const z = -Math.random() * 500;
      positions[i * 6] = x;
      positions[i * 6 + 1] = y;
      positions[i * 6 + 2] = z;
      positions[i * 6 + 3] = x;
      positions[i * 6 + 4] = y;
      positions[i * 6 + 5] = z + 5;
    }
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    return geometry;
  }, []);

  const hasReachedBase = useRef(false);
  const starAnimStart = useRef<number | null>(null);
  const ufoFadeStart = useRef<number | null>(null);

  useFrame((state) => {
    const elapsed = state.clock.getElapsedTime();

    // 🚀 UFO logic
    if (ufoRef.current) {
      const material = ufoRef.current.material as THREE.MeshBasicMaterial;

      if (!hasReachedBase.current) {
        const posY = ufoRef.current.position.y;
        const targetY = -0.2;
        const speed = 0.02; // faster descent
        if (posY + speed >= targetY) {
          ufoRef.current.position.y = targetY;
          hasReachedBase.current = true;
          starAnimStart.current = elapsed; // 🎬 trigger stars
          if (starsRef.current) starsRef.current.visible = true;
        } else {
          ufoRef.current.position.y += speed;
        }
      } else if (ufoFadeStart.current === null) {
        // slight hover while stars animate
        ufoRef.current.position.y = -0.2 + Math.sin(elapsed * 10) * 0.01;
        ufoRef.current.rotation.z = Math.sin(elapsed * 8) * 0.03;
      } else {
        // 🚀 UFO fading (0.5s fade)
        const t = elapsed - ufoFadeStart.current;
        const opacity = THREE.MathUtils.lerp(1, 0, Math.min(1, t / 0.5));
        material.opacity = opacity;
        if (opacity <= 0) {
          ufoRef.current.visible = false;
          loadingClose();
        }
      }
    }

    // ⭐ Stars logic
    if (starsRef.current && starAnimStart.current !== null) {
      const t = elapsed - starAnimStart.current;
      const material = starsRef.current.material as THREE.LineBasicMaterial;

      // compress star timing into ~2.5s
      let opacity = 0;
      let speed = 0;

      if (t < 0.5) {
        // fade in (0.5s)
        opacity = THREE.MathUtils.lerp(0, 0.8, t / 0.5);
        speed = THREE.MathUtils.lerp(0, 8, t / 0.5);
      } else if (t < 2) {
        // hold steady (1.5s)
        opacity = 0.8;
        speed = 8;
      } else if (t < 2) {
 
        opacity = THREE.MathUtils.lerp(0.8, 0, (t - 2) / 1);
        speed = THREE.MathUtils.lerp(8, 0, (t - 2) / 1);
      } else {
        opacity = 0;
        speed = 0;
        starsRef.current.visible = false;

        if (ufoFadeStart.current === null) {
          ufoFadeStart.current = elapsed; // start UFO fade after stars gone
        }
      }

      material.opacity = opacity;

      if (speed > 0) {
        const positions = starsRef.current.geometry.attributes.position
          .array as Float32Array;
        for (let i = 0; i < starCount; i++) {
          positions[i * 6 + 2] += speed;
          positions[i * 6 + 5] += speed;
          if (positions[i * 6 + 2] > 5) {
            const x = (Math.random() - 0.5) * 200;
            const y = (Math.random() - 0.5) * 200;
            const z = -500;
            positions[i * 6] = x;
            positions[i * 6 + 1] = y;
            positions[i * 6 + 2] = z;
            positions[i * 6 + 3] = x;
            positions[i * 6 + 4] = y;
            positions[i * 6 + 5] = z + 5;
          }
        }
        starsRef.current.geometry.attributes.position.needsUpdate = true;
      }
    }
  });

  return (
    <>
      {/* UFO */}
      <mesh position={[0, -1.5, 3.5]} ref={ufoRef}>
        <planeGeometry args={[0.5, 0.7, 1]} />
        <meshBasicMaterial
          map={ufoTexture || undefined}
          color={ufoTexture ? "white" : "red"}
          transparent
          opacity={1}
        />
      </mesh>

      {/* Stars (initially hidden) */}
      <lineSegments ref={starsRef} geometry={starGeometry} visible={false}>
        <lineBasicMaterial color="white" transparent opacity={0} />
      </lineSegments>
    </>
  );
};

export default OrbitLoading;
